import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import AddUserIcon from '@/images/SVG/addUser.svg';
import { CardFooter } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { AuthWrapper } from '@/shared/components/CardWrapper';
import { PasswordField } from '@/features/auth/components/PasswordField';
import { AuthTextField } from '@/features/auth/components/AuthTextField';
import {
  registerWithCognito,
  confirmRegistration,
  resendRegistrationCode,
  completeSignupAfterConfirm,
  savePendingSignup,
  loadPendingSignup,
  clearPendingSignup,
} from '@/features/auth/lib/signUp';

const EMPTY_FORM = {
  name: '',
  email: '',
  password: '',
  orgName: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const INPUT_CLASS = 'text-[#868C98] font-normal';

const SignUp = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [step, setStep] = useState('form'); // form | confirm
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPassValid, setIsPassValid] = useState(false);

  useEffect(() => {
    const pending = loadPendingSignup();
    if (!pending?.email) return;
    // Reload: password gone — back to signup form with fields prefilled
    setFormData((prev) => ({
      ...prev,
      name: pending.name || '',
      email: pending.email || '',
      orgName: pending.orgName || '',
      password: '',
    }));
    setIsPassValid(false);
    setStep('form');
  }, []);

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onField =
    (name) => (e) =>
      setField(name, e.target.value);

  const resetToForm = () => {
    clearPendingSignup();
    setVerificationCode('');
    setFormData((prev) => ({ ...prev, password: '' }));
    setIsPassValid(false);
    setStep('form');
  };

  const finishSignup = async (data) => {
    await completeSignupAfterConfirm(data);
    navigate('/');
  };

  const withLoading = async (fn) => {
    try {
      setLoading(true);
      await fn();
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = (e) => {
    e.preventDefault();
    withLoading(async () => {
      try {
        const { needsConfirmation } = await registerWithCognito(formData);
        if (needsConfirmation) {
          savePendingSignup({
            name: formData.name,
            email: formData.email,
            orgName: formData.orgName,
          });
          setStep('confirm');
          return;
        }
        await finishSignup(formData);
      } catch (error) {
        console.error('Error signing up:', error.message);
        if (error.name === 'UserAlreadyConfirmed') {
          clearPendingSignup();
          toast.error(t('auth.emailAlreadyRegistered'));
          return;
        }
        toast.error(error.message || t('auth.signUpFailed'));
      }
    });
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    withLoading(async () => {
      if (!formData.password) {
        toast.error(t('auth.reenterPasswordToFinish'));
        setStep('form');
        return;
      }

      try {
        await confirmRegistration({
          email: formData.email,
          code: verificationCode,
        });
        await finishSignup(formData);
      } catch (error) {
        console.error('Error confirming signup:', error.message);
        toast.error(error.message || t('auth.signUpFailed'));
      }
    });
  };

  const handleResendCode = async () => {
    if (loading) return;
    try {
      await resendRegistrationCode(formData.email);
      toast.success(t('auth.verificationCodeSent'));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formDisabled =
    loading ||
    !isPassValid ||
    !formData.name ||
    !formData.orgName ||
    !EMAIL_RE.test(formData.email);

  if (step === 'confirm') {
    return (
      <AuthWrapper
        heading={t('auth.verifyEmailHeading')}
        subtitle={t('auth.enterCodeSentTo', { email: formData.email })}
        icon={AddUserIcon}
      >
        <form className="text-left w-full" onSubmit={handleConfirm}>
          <div className="flex flex-col text-left mt-5 gap-4">
            <div className="flex flex-col space-y-1.5">
              <Label htmlFor="verify-code" className="font-semibold">
                {t('auth.codeLabel')}
              </Label>
              <Input
                required
                id="verify-code"
                placeholder={t('auth.enterVerificationCode')}
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className={INPUT_CLASS}
                autoComplete="one-time-code"
              />
            </div>
            <button
              type="button"
              className="font-semibold underline underline-offset-2 text-left text-sm -mt-2"
              onClick={handleResendCode}
              disabled={loading}
            >
              {t('auth.resendCode')}
            </button>
          </div>
          <CardFooter className="flex justify-center w-full flex-col p-0 mt-3">
            <Button
              type="submit"
              variant="default"
              className="w-full"
              disabled={loading || !verificationCode.trim()}
            >
              {t('auth.confirmSignUpButton')}
            </Button>
            <p className="text-[14px] font-normal mt-2 text-[#09090B]">
              {t('auth.changedYourMind')}{' '}
              <button
                type="button"
                onClick={resetToForm}
                className="text-black font-semibold underline underline-offset-2"
              >
                {t('auth.goBack')}
              </button>
            </p>
          </CardFooter>
        </form>
      </AuthWrapper>
    );
  }

  return (
    <AuthWrapper heading={t('auth.signUpHeading')} icon={AddUserIcon}>
      <form onSubmit={handleSignUp}>
        <div className="grid w-full items-center gap-4 text-left mt-5">
          <AuthTextField
            id="name"
            label={t('common.name')}
            value={formData.name}
            onChange={onField('name')}
            placeholder={t('auth.enterName')}
            autoComplete="off"
            required
          />
          <AuthTextField
            id="orgName"
            label={t('auth.organisationLabel')}
            value={formData.orgName}
            onChange={onField('orgName')}
            placeholder={t('auth.enterOrganisationName')}
          />
          <AuthTextField
            id="email"
            label={t('common.email')}
            type="email"
            value={formData.email}
            onChange={onField('email')}
            placeholder={t('auth.enterEmail')}
            autoComplete="new-email"
            required
          />
          <PasswordField
            value={formData.password}
            onChange={onField('password')}
            onValidityChange={setIsPassValid}
          />
        </div>
        <CardFooter className="flex justify-center w-full flex-col p-0 mt-5">
          <Button type="submit" variant="default" className="w-full" disabled={formDisabled}>
            {t('auth.signUpButton')}
          </Button>
          <p className="text-[14px] font-normal mt-2 text-[#09090B]">
            {t('auth.alreadyHaveAccount')}{' '}
            <Link
              to="/"
              className="text-black font-semibold underline underline-offset-2"
            >
              {t('auth.signInButton')}
            </Link>
          </p>
        </CardFooter>
      </form>
    </AuthWrapper>
  );
};

export default SignUp;
